import { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { statsService, type KeywordEffectiveness } from '@/services/stats.service';

const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'bg-success-soft text-success',
  medium: 'bg-warning-soft text-warning',
  low: 'bg-surface text-text-secondary',
};

const CONFIDENCE_LABELS: Record<string, string> = {
  high: 'High',
  medium: 'Med',
  low: 'Low',
};

export function KeywordInsightsCard() {
  const [keywords, setKeywords] = useState<KeywordEffectiveness[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataWindow, setDataWindow] = useState<string>('');

  useEffect(() => {
    statsService
      .getKeywords(12)
      .then((res) => {
        const sorted = [...res.keywords]
          .filter((k) => k.effectiveness > 0)
          .sort((a, b) => b.effectiveness - a.effectiveness)
          .slice(0, 10);
        setKeywords(sorted);
        setDataWindow(res.dataWindow);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (!loading && keywords.length === 0) return null;

  return (
    <div className="bg-surface border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Trending Skills</h2>
            <p className="text-xs text-text-secondary mt-0.5">
              Skills with the biggest ATS score impact across all analyses
              {dataWindow ? ` · ${dataWindow}` : ''}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center justify-between animate-pulse">
              <div className="h-4 bg-border rounded w-28" />
              <div className="h-5 bg-border rounded w-16" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {keywords.map((kw) => (
            <div
              key={kw.keyword}
              className="flex items-center justify-between bg-background border border-border rounded-md px-3 py-2"
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-text-primary capitalize truncate">
                  {kw.keyword}
                </span>
                {kw.topRoles.length > 0 && (
                  <span className="text-xs text-text-secondary truncate">
                    {kw.topRoles[0]}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                <span className="text-sm font-semibold text-success">
                  +{kw.effectiveness}
                </span>
                <span
                  className={`text-xs px-1.5 py-0.5 rounded font-medium ${CONFIDENCE_COLORS[kw.confidence]}`}
                >
                  {CONFIDENCE_LABELS[kw.confidence]}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="mt-3 text-xs text-text-secondary">
        Score impact = average ATS score improvement when this skill is present in the resume.
      </p>
    </div>
  );
}
